import { ArrowUpDown, Globe, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCountryScope, usePermissions } from '@/features/auth';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { DataTableToolbar } from '@/shared/components/data-table/data-table-toolbar';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { SearchInput } from '@/shared/components/search-input';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';

import { useDeleteRegion, useRegions, useSetRegionStatus } from '../api/regions.queries';
import { RegionFormDialog } from '../components/region-form-dialog';
import { RegionOrderDialog } from '../components/region-order-dialog';
import { createRegionColumns } from '../components/regions-table-columns';
import type { Region } from '../types/region.types';

const ANY = 'all';
const REASON_MAX = 500;

/**
 * Regions — the stores (US, UAE, Global…) a brand's denominations belong to.
 *
 * Shared by every brand and country, so changes need catalogue.manage and
 * all-country scope. Deactivating a region hides all its denominations
 * everywhere, and the confirmation says how many. Only a non-system region no
 * denomination belongs to can be deleted — the API refuses the rest.
 */
export function RegionsPage() {
  // Storefront position first by default: the order customers see stores in.
  const tableState = useTableSearchParams({
    defaultPageSize: 50,
    defaultSort: 'sortOrder',
    defaultDirection: 'asc',
  });
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();
  const canManage = can('catalogue.manage') && allCountries;

  const [editing, setEditing] = useState<Region | 'new' | null>(null);
  const [reordering, setReordering] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<Region | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Region | null>(null);
  const [reason, setReason] = useState('');

  const statusFilter = tableState.getFilter('status');
  const debouncedSearch = useDebouncedValue(tableState.search, 300);

  const params = useMemo(
    () => ({
      page: tableState.page,
      pageSize: tableState.pageSize,
      search: debouncedSearch || undefined,
      isActive: statusFilter === null ? undefined : statusFilter === 'active',
      sort: tableState.sort ?? undefined,
      direction: tableState.direction,
    }),
    [
      tableState.page,
      tableState.pageSize,
      debouncedSearch,
      statusFilter,
      tableState.sort,
      tableState.direction,
    ],
  );

  const regions = useRegions(params);
  const setStatus = useSetRegionStatus();
  const deleteRegion = useDeleteRegion();

  const columns = useMemo(
    () =>
      createRegionColumns({
        canManage,
        onEdit: setEditing,
        onToggleActive: (region) => {
          setReason('');
          setPendingToggle(region);
        },
        onDelete: (region) => {
          setReason('');
          setPendingDelete(region);
        },
      }),
    [canManage],
  );

  const table = useDataTable({
    data: regions.data,
    columns,
    tableState,
    getRowId: (region) => String(region.id),
  });

  const count = pendingToggle?.activeDenominationCount ?? 0;

  return (
    <PageContainer>
      <PageHeader
        title="Regions"
        description="The stores a brand's denominations belong to — a US card is not a UAE card. Shared by every brand and country."
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setReordering(true)}>
                <ArrowUpDown className="size-4" aria-hidden />
                Storefront order
              </Button>
              <Button size="sm" onClick={() => setEditing('new')}>
                <Plus className="size-4" aria-hidden />
                Add region
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-3">
        <DataTableToolbar
          hasActiveFilters={tableState.hasActiveFilters}
          onResetFilters={tableState.resetFilters}
        >
          <SearchInput
            value={tableState.search}
            onChange={tableState.setSearch}
            placeholder="Search regions…"
            className="w-full sm:w-60"
          />
          <Select
            value={statusFilter ?? ANY}
            onValueChange={(value) => tableState.setFilter('status', value === ANY ? null : value)}
          >
            <SelectTrigger size="sm" className="h-8 w-32 text-xs" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY} className="text-xs">
                Any status
              </SelectItem>
              <SelectItem value="active" className="text-xs">
                Active
              </SelectItem>
              <SelectItem value="inactive" className="text-xs">
                Inactive
              </SelectItem>
            </SelectContent>
          </Select>
        </DataTableToolbar>

        <DataTable
          table={table}
          isLoading={regions.isLoading}
          isFetching={regions.isFetching}
          error={regions.error}
          onRetry={() => void regions.refetch()}
          emptyState={
            tableState.hasActiveFilters ? (
              <EmptyState
                icon={Globe}
                title="No regions match your filters"
                description="Try a different search term, or clear the filters."
                action={
                  <Button variant="outline" size="sm" onClick={tableState.resetFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={Globe}
                title="No regions yet"
                description={
                  canManage
                    ? 'Add a region so denominations can be grouped by store.'
                    : 'No regions have been added yet.'
                }
              />
            )
          }
        />

        <DataTablePagination table={table} />
      </div>

      {reordering && <RegionOrderDialog onClose={() => setReordering(false)} />}

      {editing !== null && (
        <RegionFormDialog
          region={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <ConfirmDialog
        open={pendingToggle !== null}
        onOpenChange={(open) => !open && setPendingToggle(null)}
        title={pendingToggle?.isActive ? 'Deactivate this region?' : 'Activate this region?'}
        description={
          pendingToggle?.isActive ? (
            <>
              <strong>{pendingToggle.name}</strong> and its <strong>{count}</strong> active
              denomination{count === 1 ? '' : 's'} will be hidden from customers in every country.
              Existing orders are unaffected.
            </>
          ) : (
            <>
              <strong>{pendingToggle?.name}</strong> becomes available again: its active
              denominations reappear wherever they are offered.
            </>
          )
        }
        confirmLabel={pendingToggle?.isActive ? 'Deactivate' : 'Activate'}
        variant={pendingToggle?.isActive ? 'destructive' : 'default'}
        isPending={setStatus.isPending}
        onConfirm={() => {
          if (!pendingToggle) return;
          const trimmed = reason.trim();
          setStatus.mutate(
            {
              regionId: pendingToggle.id,
              isActive: !pendingToggle.isActive,
              reason: trimmed === '' ? null : trimmed,
            },
            { onSuccess: () => setPendingToggle(null) },
          );
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="region-change-reason" className="text-xs">
            Reason{' '}
            <span className="text-muted-foreground font-normal">
              (optional, kept in the audit log)
            </span>
          </Label>
          <Input
            id="region-change-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={REASON_MAX}
            disabled={setStatus.isPending}
            className="h-8 text-xs"
          />
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this region?"
        description={
          <>
            <strong>{pendingDelete?.name}</strong> has no denominations. It will be removed
            permanently, for every brand and country. This can't be undone.
          </>
        }
        confirmLabel="Delete"
        variant="destructive"
        isPending={deleteRegion.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const trimmed = reason.trim();
          deleteRegion.mutate(
            {
              regionId: pendingDelete.id,
              name: pendingDelete.name,
              reason: trimmed === '' ? null : trimmed,
            },
            // Closed on a refusal too: the toast explains it and the counts refresh.
            { onSettled: () => setPendingDelete(null) },
          );
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="region-delete-reason" className="text-xs">
            Reason{' '}
            <span className="text-muted-foreground font-normal">
              (optional, kept in the audit log)
            </span>
          </Label>
          <Input
            id="region-delete-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={REASON_MAX}
            disabled={deleteRegion.isPending}
            className="h-8 text-xs"
          />
        </div>
      </ConfirmDialog>
    </PageContainer>
  );
}
